// https://documentation.onesignal.com/docs/web-sdk-reference
//
// NB: Below, we use dynamic import() to load react-onesignal only when ONESIGNAL_APP2_ID is configured.
// This avoids loading the SDK unnecessarily in tests and environments without OneSignal config.
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import qlog from "../qlog";

/**
 * Check if the current domain is configured for OneSignal.
 * OneSignal development instance is only configured for port 3000 (V1).
 * In production, it's configured for app.qwoted.com and app.qwoted-staging.tech.
 */
// eslint-disable-next-line import/prefer-default-export
export function isOneSignalAllowedDomain() {
  const { hostname, port } = window.location;
  return (
    hostname === "app.qwoted.com" ||
    hostname === "app.qwoted-staging.tech" ||
    (hostname === "localhost" && port === "3000")
  );
}

async function pushSubscriptionChangeListener(event) {
  // NB: This is the format of event.current
  // {
  //   id: "4873e340-8406-42b3-979f-4c76df8f76e5", // NB: this becomes subscription_id
  //   optedIn: false, // NB: this becomes opted_in
  //   token: "https://fcm.googleapis.com/fcm/send/..."
  // }

  const REFUSAL_STORAGE_KEY = "webPushRefusal";

  qlog.debug("Push subscription state changed", event.current);

  try {
    if (event.current.token) {
      const userId = globalThis.Qwoted.CURRENT_USER_ID;
      if (!userId) {
        qlog.error("User ID is not available. Cannot update web push subscription.");
        return;
      }

      if (event.current.optedIn) {
        // User opted in: clear refusal data
        qlog.info("User has subscribed to notifications. Clearing refusal data.");
        localStorage.removeItem(REFUSAL_STORAGE_KEY);
      } else {
        // User opted out: pre-fill refusal data
        qlog.info("User has unsubscribed from notifications. Pre-filling refusal data.");
        const refusalData = {
          refusalCount: 1,
          lastRefusedAt: new Date().toISOString(),
        };
        localStorage.setItem(REFUSAL_STORAGE_KEY, JSON.stringify(refusalData));
        qlog.debug("Pre-filled refusal data:", refusalData);
      }

      // Post the subscription data to the server
      const subscriptionData = {
        data: {
          attributes: {
            migrate_user_id: globalThis.Qwoted.CURRENT_USER_MIGRATE_ID,
            opted_in: event.current.optedIn,
            subscription_id: event.current.id,
            token: event.current.token,
          },
        },
      };

      try {
        const response = await fetch(`/notify/api/users/${userId}/web_push_subscriptions`, {
          method: "POST",
          headers: new Headers({
            ...authenticatedHeadersHash,
            "Content-Type": "application/json",
          }),
          body: JSON.stringify(subscriptionData),
        });

        if (!response.ok) {
          const error = await response.json();
          qlog.error("Failed to update web push subscription:", error);
        } else {
          qlog.debug("Successfully updated web push subscription on server.");
        }
      } catch (error) {
        qlog.error("Error updating web push subscription:", error);
      }
    } else {
      qlog.warn("No token available in push subscription change event. Skipping server update.");
    }
  } catch (error) {
    qlog.error("Error in pushSubscriptionChangeListener:", error);
  }
}

async function initializeOneSignal() {
  if (!globalThis.Qwoted?.ONESIGNAL_APP2_ID) {
    qlog.debug("ONESIGNAL_APP2_ID is not configured. OneSignal will not be loaded.");
    return;
  }

  if (!isOneSignalAllowedDomain()) {
    qlog.debug(`Domain ${window.location.host} is not configured for OneSignal. Skipping initialization.`);
    return;
  }

  // Dynamically import the OneSignal SDK only when we have configuration
  const { default: OneSignal } = await import("react-onesignal");

  await OneSignal.init({
    persistNotification: false,
    autoResubscribe: true,
    allowLocalhostAsSecureOrigin: globalThis.Qwoted.BUSINESS_ENVIRONMENT === "development",
    appId: globalThis.Qwoted.ONESIGNAL_APP2_ID,
    notifyButton: {
      enable: false,
    },
    welcomeNotification: { message: "Never miss a beat with Qwoted notifications" },
  });

  qlog.debug("OneSignal initialized.");
  const userId = globalThis.Qwoted.CURRENT_USER_MIGRATE_ID || globalThis.Qwoted.CURRENT_USER_ID;

  // Custom JavaScript event notifies the codebase that the OneSignal API is ready for use
  const oneSignalInitEvent = new CustomEvent("qwotedOneSignalInitialized", {});
  window.dispatchEvent(oneSignalInitEvent);

  if (userId) {
    await OneSignal.login(userId.toString());
    qlog.debug(`OneSignal logged in as userId: ${userId}`);

    // Custom JavaScript event notifies the codebase that the OneSignal API is ready for use
    const oneSignalLoginEvent = new CustomEvent("qwotedOneSignalLoggedIn", {
      detail: {
        userId,
      },
    });

    setTimeout(() => {
      window.dispatchEvent(oneSignalLoginEvent);
      qlog.trace("OneSignal login event dispatched after micro delay");
    }, 1000);

    await OneSignal.User.PushSubscription.addEventListener("change", pushSubscriptionChangeListener);
  } else {
    qlog.debug("OneSignal not logged in.");
  }
}

// Ensure OneSignal is initialized on DOM load
document.addEventListener("DOMContentLoaded", initializeOneSignal);
